import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import getSymbolFromCurrency from 'currency-symbol-map';
import moment from "moment/moment";
import {Swiper, SwiperSlide} from 'swiper/react';
import {Pagination} from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import LockerIcon from '../assets/locker.svg';
import ButtonComponent from "./ButtonComponent";
import { BUTTON_WARNING, DUROLT_CONTACT_NUMBER, POD_BANK_TYPE } from "../assets/constants/Constants";
import { CHEVRONS_RIGHT, GOOGLE_MAPS_ARROW_ICON, GOOGLE_MAPS_LOCATION_ICON } from "../assets/constants/Icons";
import { getLockerSelectionButtonText, redirectToGoogleMapsDirections } from "../lib/Utils";
import { getLockerScheduleDetails } from "../api/api";
import { DUMMY_BOOKINGS } from "../assets/constants/Data";
import ScheduleCalendarComponent from "./ScheduleCalendarComponent";

const LockerDetailsComponent = ({ locker, onBook }) => {
  const { t } = useTranslation();
  const [scheduleDetails, setScheduleDetails] = useState([]);
  const [showScheduleCalendar, setShowScheduleCalendar] = useState(false);
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);

  const {
    name = t("locker_details_default_name"),
    address = t("address_shown_after_booking"),
    price = "",
    fallbackImage = LockerIcon,
    distance = "",
    description = "",
    lockerNumber = "",
    currency = "",
    lockerImages = []
  } = locker || {};

  const imageSlides = Array.isArray(lockerImages) && lockerImages.length > 0
    ? lockerImages
        .map((productImage, index) => ({
          src:
            typeof productImage === "string"
              ? productImage
              : productImage?.url || productImage?.src || productImage?.image || "",
          alt:
            typeof productImage === "string"
              ? `${name} ${index + 1}`
              : productImage?.name || `${name} ${index + 1}`
        }))
        .filter((image) => image.src)
    : [{ src:fallbackImage, alt: name }];

  // Get location string
  const getLocation = () => {
    if (address) return address;
    return t("location_available_after_booking");
  };

  const fetchScheduleDetails = async (date) => {
    setIsLoadingSchedule(true);
    try {
      const response = await getLockerScheduleDetails(locker?.id, date);
      const bookings = response?.data?.length > 0 ? response.data : DUMMY_BOOKINGS;
      setScheduleDetails(bookings);
    } catch (error) {
      setScheduleDetails(DUMMY_BOOKINGS);
    } finally {
      setIsLoadingSchedule(false);
    }
  };

  const handleGetScheduleDetails = async () => {
    if (isLoadingSchedule) {
      return;
    }

    await fetchScheduleDetails(moment().format("YYYY-MM-DD"));
    setShowScheduleCalendar(true);
  };

  const handleScheduleDateChange = (date) => {
    if (isLoadingSchedule) {
      return;
    }

    fetchScheduleDetails(date);
  };

  return (
    <div className="storage-card">
      
      <div className="image-container">
        <Swiper
          modules={[Pagination]}
          pagination={ { clickable: true }}
          loop={imageSlides.length > 1}
          className='image-carousel'
        >
          {imageSlides.map((productImage, index) => (
            <SwiperSlide key={`${productImage.src}-${index}`} className='image-slide'>
              <img
                src={productImage.src}
                alt={productImage.alt}
                className='equipment-image'
                onError={(e) => {
                  e.target.src = fallbackImage;
                }}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      
      </div>

      {/* Content Section */}
      <div className="content">
        <div className="header">
          <h2 className="title">{name}</h2>

          <div className="badge-container">
            <span className="badge badge-open">{t("open_24h_badge")}</span>
            {distance && (
              <span className="badge badge-distance">{distance} km</span>
            )}
          </div>

          <div className="info-line">
            <span className="everyday">{t("every_day_text")}</span>
            {lockerNumber && (
              <span className="locker">
                {t("locker_number_label", { lockerNumber })}
              </span>
            )}
          </div>
        </div>

        {description && (
          <div className="description">
            <p>{description}</p>
          </div>
        )}

        <div className="address-section">
          <p className="address-label">
            <span className="address-icon"><GOOGLE_MAPS_LOCATION_ICON /></span>
            {getLocation()}
          </p>
          <ButtonComponent
              type={BUTTON_WARNING}
              handleClick={() => redirectToGoogleMapsDirections(locker?.position, locker?.LockerBankURL)}
              title={t('text_google_direction_map')}
              startIcon={<GOOGLE_MAPS_LOCATION_ICON />}
              endIcon={<GOOGLE_MAPS_ARROW_ICON />}
              
            />
          
          <div className="divider"></div>
        </div>

        <div className="hours">
          <h3 className="subtitle">{t("availability_hours_title")}</h3>

          {locker?.LockerBankType === POD_BANK_TYPE ? (
            <button type="button" className="schedule-link" onClick={handleGetScheduleDetails} disabled={isLoadingSchedule}>
              <span>{isLoadingSchedule ? t("loading_text") : t("get_schedule_details_text")}</span>
              <span className="schedule-link-icon"><CHEVRONS_RIGHT /></span>
            </button>
          ) : (
            <p className="hours-text">{t("open_24_hours_every_day_text")}</p>
          )}
        </div>

        <div className="includes">
          <h3 className="subtitle">{t("booking_includes_title")}</h3>

          <div className="feature">
            <span className="feature-icon">✓</span>
            <div className="feature-content">
              <strong>{t("free_cancellation_title")}</strong>
               <p>{t('text_Cancel_UpTo_Three_Hours_Locker')} </p>
            </div>
          </div>
         <div className="feature">
            <span className="feature-icon">💬</span>
            <div className="feature-content">
              <strong>{t("support_24_7_title")}</strong>
              <p>{t("support_24_7_description")}</p>
              <p className="phone-number">{DUROLT_CONTACT_NUMBER}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="footer">
        <div className="price-section">
          <div className="price-box">
            <div className="price">  
              { "From " + getSymbolFromCurrency(currency)} {price}
            </div>
            <div className="duration">{t('text_Per_Day')}</div>
          </div>

          <button className="button" onClick={onBook}>
            {getLockerSelectionButtonText(locker, t)}
          </button>
        </div>
      </div>

      <ScheduleCalendarComponent
        visible={showScheduleCalendar}
        onClose={() => setShowScheduleCalendar(false)}
        bookings={scheduleDetails}
        lockerName={name}
        onDateChange={handleScheduleDateChange}
        isLoading={isLoadingSchedule}
      />
    </div>
  );
};

export default LockerDetailsComponent;
